import { cva } from "class-variance-authority";
import type { ElementType, MouseEvent } from "react";
import { useCallback } from "react";
import { cn } from "../../utils";
import { Spinner } from "../Spinner";
import type { SpinnerColor, SpinnerSize } from "../Spinner/types";
import { Text } from "../Typography";
import { TailwindFontSize } from "../Typography/typography";
import { ButtonIcon } from "./ButtonIcon";
import {
  ButtonColor,
  ButtonProps,
  ButtonSize,
  ButtonSpinnerPosition,
  ButtonVariant,
} from "./types";

const borderRadiusVariants = {
  xs: "rounded-lg group-[]/input:rounded-lg",
  sm: "rounded-lg group-[]/input:rounded-lg",
  md: "rounded-xl group-[]/input:rounded-lg group-[]/input:focus-visible:outline-1",
  lg: "rounded-xl group-[]/input:rounded-lg group-[]/input:focus-visible:outline-1",
};

const buttonVariants = cva(
  "flex items-center justify-center flex-shrink-0 relative cursor-pointer transition-all duration-150 ease-out outline-none select-none whitespace-nowrap",
  {
    variants: {
      variant: {
        filled: "border",
        outlined: "border bg-transparent",
        ghost: "border bg-transparent",
        text: "border-transparent",
      },
      color: {
        primary: "",
        neutral: "",
        destructive: "",
      },
      size: {
        xs: "h-6 px-2 gap-1.5",
        sm: "h-8 px-3 gap-2",
        md: "h-10 px-4 gap-2",
        lg: "h-12 px-6 gap-2",
      },
      isIcon: {
        true: "p-0",
        false: "",
      },
      disabled: {
        true: "pointer-events-none",
        false: "",
      },
    },
    compoundVariants: [
      {
        variant: "filled",
        color: "primary",
        className:
          "bg-primary-500 border-primary-500 text-white hover:bg-primary-400 hover:border-primary-400 focus-visible:bg-primary-400 focus-visible:border-primary-400 active:bg-primary-600 active:border-primary-600",
      },
      {
        variant: "filled",
        color: "neutral",
        className:
          "bg-neutral-500 border-neutral-500 text-white hover:bg-neutral-400 hover:border-neutral-400 focus-visible:bg-neutral-400 focus-visible:border-neutral-400 active:bg-neutral-600 active:border-neutral-600",
      },
      {
        variant: "filled",
        color: "destructive",
        className:
          "bg-danger-600 border-danger-600 text-white hover:bg-danger-500 hover:border-danger-500 focus-visible:bg-danger-500 focus-visible:border-danger-500 active:bg-danger-600 active:border-danger-600",
      },
      {
        variant: "outlined",
        color: "primary",
        className:
          "border-primary-500 text-primary-500 hover:bg-primary-500/5 hover:border-primary-600 focus-visible:bg-primary-500/5 focus-visible:border-primary-600 active:bg-primary-500/10",
      },
      {
        variant: "outlined",
        color: "neutral",
        className:
          "border-neutral-500 text-neutral-500 hover:bg-neutral-500/5 hover:border-neutral-600 focus-visible:bg-neutral-500/5 focus-visible:border-neutral-600 active:bg-neutral-500/10",
      },
      {
        variant: "outlined",
        color: "destructive",
        className:
          "border-danger-600 text-danger-600 hover:bg-danger-500/5 hover:border-danger-700 focus-visible:bg-danger-500/5 focus-visible:border-danger-700 active:bg-danger-500/10",
      },
      {
        variant: "ghost",
        color: "primary",
        className:
          "border-transparent text-primary-500 hover:bg-primary-500/5 focus-visible:bg-primary-500/5 active:bg-primary-500/10",
      },
      {
        variant: "ghost",
        color: "neutral",
        className:
          "border-transparent text-neutral-500 hover:bg-neutral-500/5 focus-visible:bg-neutral-500/5 active:bg-neutral-500/10",
      },
      {
        variant: "ghost",
        color: "destructive",
        className:
          "border-transparent text-danger-600 hover:bg-danger-500/5 focus-visible:bg-danger-500/5 active:bg-danger-500/10",
      },
      {
        variant: "text",
        color: "primary",
        className: "text-primary-500 hover:underline active:text-primary-600",
      },
      {
        variant: "text",
        color: "neutral",
        className: "text-neutral-500 hover:underline active:text-neutral-600",
      },
      {
        variant: "text",
        color: "destructive",
        className: "text-danger-600 hover:underline active:text-danger-700",
      },
      {
        variant: "text",
        isIcon: true,
      },
      // Focus outline variants for text icon buttons
      {
        variant: "text",
        isIcon: true,
        size: "xs",
        className:
          "focus-visible:outline focus-visible:outline-1 focus-visible:outline-primary-500 focus-visible:outline-offset-0",
      },
      {
        variant: "text",
        isIcon: true,
        size: "sm",
        className:
          "focus-visible:outline focus-visible:outline-1 focus-visible:outline-primary-500 focus-visible:outline-offset-0",
      },
      {
        variant: "text",
        isIcon: true,
        size: "md",
        className:
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500 focus-visible:outline-offset-0",
      },
      {
        variant: "text",
        isIcon: true,
        size: "lg",
        className:
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500 focus-visible:outline-offset-0",
      },
      {
        isIcon: true,
        size: "xs",
        className: "w-6",
      },
      {
        isIcon: true,
        size: "sm",
        className: "w-8",
      },
      {
        isIcon: true,
        size: "md",
        className: "w-10",
      },
      {
        isIcon: true,
        size: "lg",
        className: "w-12",
      },
      {
        variant: "text",
        className: "px-0 h-auto w-auto rounded-sm",
      },
      // Focus outline variants for text buttons
      {
        variant: "text",
        isIcon: false,
        size: "xs",
        className:
          "focus-visible:outline focus-visible:outline-1 focus-visible:outline-primary-500 focus-visible:outline-offset-0",
      },
      {
        variant: "text",
        isIcon: false,
        size: "sm",
        className:
          "focus-visible:outline focus-visible:outline-1 focus-visible:outline-primary-500 focus-visible:outline-offset-0",
      },
      {
        variant: "text",
        isIcon: false,
        size: "md",
        className:
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500 focus-visible:outline-offset-0",
      },
      {
        variant: "text",
        isIcon: false,
        size: "lg",
        className:
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500 focus-visible:outline-offset-0",
      },
      // Focus outline variants for filled, outlined, and ghost buttons
      {
        variant: "filled",
        size: "xs",
        className:
          "focus-visible:outline focus-visible:outline-1 focus-visible:outline-primary-500 focus-visible:outline-offset-0",
      },
      {
        variant: "filled",
        size: "sm",
        className:
          "focus-visible:outline focus-visible:outline-1 focus-visible:outline-primary-500 focus-visible:outline-offset-0",
      },
      {
        variant: "filled",
        size: "md",
        className:
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500 focus-visible:outline-offset-0",
      },
      {
        variant: "filled",
        size: "lg",
        className:
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500 focus-visible:outline-offset-0",
      },
      {
        variant: "outlined",
        size: "xs",
        className:
          "focus-visible:outline focus-visible:outline-1 focus-visible:outline-primary-500 focus-visible:outline-offset-0",
      },
      {
        variant: "outlined",
        size: "sm",
        className:
          "focus-visible:outline focus-visible:outline-1 focus-visible:outline-primary-500 focus-visible:outline-offset-0",
      },
      {
        variant: "outlined",
        size: "md",
        className:
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500 focus-visible:outline-offset-0",
      },
      {
        variant: "outlined",
        size: "lg",
        className:
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500 focus-visible:outline-offset-0",
      },
      {
        variant: "ghost",
        size: "xs",
        className:
          "focus-visible:outline focus-visible:outline-1 focus-visible:outline-primary-500 focus-visible:outline-offset-0",
      },
      {
        variant: "ghost",
        size: "sm",
        className:
          "focus-visible:outline focus-visible:outline-1 focus-visible:outline-primary-500 focus-visible:outline-offset-0",
      },
      {
        variant: "ghost",
        size: "md",
        className:
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500 focus-visible:outline-offset-0",
      },
      {
        variant: "ghost",
        size: "lg",
        className:
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500 focus-visible:outline-offset-0",
      },
      // Disabled button variants
      {
        variant: "filled",
        disabled: true,
        className: "text-neutral-200 bg-neutral-100 border-neutral-100",
      },
      {
        variant: "outlined",
        disabled: true,
        className: "text-neutral-200 bg-transparent border-neutral-200",
      },
      {
        variant: "ghost",
        disabled: true,
        className: "text-neutral-200 bg-transparent border-transparent",
      },
      {
        variant: "text",
        disabled: true,
        className: "text-neutral-200",
      },
    ],
    defaultVariants: {
      variant: "filled",
      color: "primary",
      size: "md",
      isIcon: false,
      disabled: false,
    },
  },
);

export const BaseButton = <C extends ElementType = "button">(
  props: ButtonProps<C>,
) => {
  const {
    as,
    type,
    variant,
    color,
    size,
    loading,
    spinnerPosition: spinnerPositionProp,
    spinnerProps,
    startIcon,
    endIcon,
    allowClickOnLoading,
    isIcon,
    onClick,
    disabled,
    className,
    children,
    content,
    ...restProps
  } = props;

  const Component = as || "button";
  const buttonType = type || "button";
  const buttonVariant: ButtonVariant = variant || "filled";
  const buttonColor: ButtonColor = color || "primary";
  const buttonSize: ButtonSize = size || "md";

  const isIconButton = !!isIcon;
  const spinnerDefaultSize: SpinnerSize = buttonSize === "xs" ? "2xs" : "xs";
  const spinnerPosition: ButtonSpinnerPosition = isIconButton
    ? "center"
    : spinnerPositionProp || "center";

  const showStartIcon: boolean =
    !isIconButton &&
    startIcon !== undefined &&
    !(spinnerPosition === "start" && loading === true);
  const showEndIcon: boolean =
    !isIconButton &&
    endIcon !== undefined &&
    !(spinnerPosition === "end" && loading === true);
  const showSpinner: boolean = loading === true && !disabled;

  const getSpinnerColor = (
    variant: ButtonVariant,
    color: ButtonColor,
  ): SpinnerColor => {
    if (variant === "filled") {
      return "white";
    }
    if (color === "primary") return "primary";
    if (color === "neutral") return "neutral";
    return "destructive";
  };

  const spinnerColor = getSpinnerColor(buttonVariant, buttonColor);
  const spinnerClass = `flex items-center justify-center ${buttonSize === "xs" ? "w-3 h-3" : "w-4 h-4"}`;
  const iconClass = `flex items-center justify-center ${buttonSize === "xs" ? "w-3 h-3" : "w-4 h-4"}`;
  const centerSpinnerClass =
    "absolute inset-0 flex items-center justify-center";
  const contentOpacity =
    showSpinner && spinnerPosition === "center" ? "opacity-0" : "";

  const textSize: Record<ButtonSize, TailwindFontSize> = {
    xs: "xs",
    sm: "sm",
    md: "sm",
    lg: "md",
  };

  const rootClass = cn(
    buttonVariants({
      variant: buttonVariant,
      color: buttonColor,
      size: buttonSize,
      isIcon: isIconButton,
      disabled: disabled,
    }),
    buttonVariant !== "text" && borderRadiusVariants[buttonSize],
    className,
  );

  const handleClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      if (!allowClickOnLoading && loading) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      if (onClick) {
        onClick(e);
      }
    },
    [onClick, allowClickOnLoading, loading],
  );

  const componentProps =
    Component === "button"
      ? { type: buttonType, disabled, ...restProps }
      : restProps;

  const Tag = Component as ElementType;

  return (
    <Tag
      {...componentProps}
      className={rootClass}
      onClick={handleClick}
      aria-busy={loading}
      aria-disabled={disabled}
      style={{ outlineOffset: "1px", ...restProps.style }}
    >
      {showSpinner && spinnerPosition === "start" && (
        <div className={spinnerClass}>
          <Spinner
            color={spinnerColor}
            size={spinnerDefaultSize}
            {...spinnerProps}
          />
        </div>
      )}
      {showStartIcon && (
        <div className={cn(iconClass, contentOpacity)}>
          <ButtonIcon icon={startIcon} size={buttonSize} />
        </div>
      )}
      <Text
        as="span"
        weight="medium"
        size={textSize[buttonSize]}
        className={contentOpacity}
      >
        {children || content}
      </Text>
      {showEndIcon && (
        <div className={cn(iconClass, contentOpacity)}>
          <ButtonIcon icon={endIcon} size={buttonSize} />
        </div>
      )}
      {showSpinner && spinnerPosition === "center" && (
        <div className={centerSpinnerClass}>
          <Spinner
            color={spinnerColor}
            size={spinnerDefaultSize}
            {...spinnerProps}
          />
        </div>
      )}
      {showSpinner && spinnerPosition === "end" && (
        <div className={spinnerClass}>
          <Spinner
            color={spinnerColor}
            size={spinnerDefaultSize}
            {...spinnerProps}
          />
        </div>
      )}
    </Tag>
  );
};
